import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton
} from "@mui/material";
import AutocompleteMultiSelect from "applications/signal/components/AutocompleteMultiSelect";
import { useSignalGroupMembers, useGetSignalUserTeamMembers, useSaveSignalUserTeamMembers } from "applications/signal/hooks/gqlHooks";
import { signalUserOptionsFormatter, extractMemberIdsFromOptions } from "applications/signal/utils/dataUtils";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { UserTeamMemberManageModalData, UserTeamMemberManageModalSchema } from "./schema";
import { SignalUser } from "gql/graphql";

type Props = {
  userTeamId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
}

export default function UserTeamMemberManageModal({ userTeamId, open, setOpen, setSnackBarMessage }: Props) {
  const [memberData, setMemberData] = useState<string[]>([]);
  const {
    data: groupMemberResponse,
    fetching: groupMemberFetching,
    error: groupMemberError,
    fetchSignalGroupMembersAll
  } = useSignalGroupMembers();
  const {
    data: teamMemberResponse,
    fetching: teamMemberFetching,
    error: teamMemberError,
    fetchSignalUserTeamMembers
  } = useGetSignalUserTeamMembers(userTeamId);
  const { saveSignalUserTeamMembers } = useSaveSignalUserTeamMembers();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserTeamMemberManageModalData>({
    resolver: zodResolver(UserTeamMemberManageModalSchema),
    defaultValues: {
      members: [],
    },
  });
  useEffect(() => {
    fetchSignalGroupMembersAll();
    fetchSignalUserTeamMembers(userTeamId);
  }, [userTeamId]);
  useEffect(() => {
    if (groupMemberResponse?.getSignalGroupMembers && teamMemberResponse?.getSignalUserTeamMembers) {
      setMemberData(signalUserOptionsFormatter(groupMemberResponse.getSignalGroupMembers.members as SignalUser[]));
      reset({
        members: signalUserOptionsFormatter(teamMemberResponse.getSignalUserTeamMembers as SignalUser[]),
      });
    }
    if (groupMemberError || teamMemberError) {
      setGlobalMessage({ severity: 'error', message: 'チームメンバーの取得に失敗しました。' });
    }
  }, [groupMemberResponse, groupMemberError, teamMemberResponse, teamMemberError]);

  const handleClickClose = () => {
    setOpen(false);
  }

  const onSubmit = async (data: UserTeamMemberManageModalData) =>  {
    const { data: result, error } = await saveSignalUserTeamMembers({data: {
      signal_user_team_id: String(userTeamId),
      signal_group_member_ids: extractMemberIdsFromOptions(data.members),
    }});
    if (error) {
      setGlobalMessage({
        message: `チームメンバーの更新に失敗しました。`,
        severity: "error",
      });
    } else {
      setSnackBarMessage({
        message: `チームメンバーを更新しました。`,
        severity: "success",
      });
      handleClickClose();
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      onClose={handleClickClose}
    >
      <DialogTitle align="center">
        チームメンバー管理
      </DialogTitle>
      <IconButton
        onClick={handleClickClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
          <FormControl
            fullWidth
            margin="normal"
            error={!!errors.members}
          >
            <Controller
              name="members"
              control={control}
              render={({ field }) => (
                <AutocompleteMultiSelect
                  label="チームメンバー"
                  options={memberData || []}
                  value={field.value || []}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                />
              )}
            />
            <FormHelperText>
              {errors.members?.message}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
